import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReturnButtons from '../../components/core/ReturnButtons';
import { makeStyles, ThemeProvider, useTheme } from '@material-ui/core/styles';
import { Box, Grid, Paper, Typography, IconButton, Collapse } from '@material-ui/core';
import { ExpandLess, ExpandMore, LayersOutlined } from '@material-ui/icons';
import GoogleMap from '../../components/map/GoogleMap';
import BackgroundMapPanel from '../../components/map/BackgroundMapPanel';
import OverlayPanelsToggle from '../../components/map/OverlayPanelsToggle';
import ImageChooserCard from '../../components/map/ImageChooserCard';
import LoadedImagesAccordion from '../../components/map/LoadedImagesAccordion';
import { ShapeList } from '../../components';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import AttentionMessage from '../../components/core/AttentionMessage';
import ShoreRAnalysisSteps from '../../components/visualization/shoreR/ShoreRAnalysisSteps';
import MeanTransectButton from '../../components/visualization/MeanTransectButton';
import HovmollerPlot from '../../components/visualization/Hovmoller';
import { useRoiOverlay } from "../../components/map/useRoiOverlay";
import {
  buildSessionExport,
  restorePendingBaseline,
  renderPendingImportedShapes,
} from '../../../algorithms/shoreR/sessionExport';
import { exportSession, useLocalStorage } from '../../../common/utils';
import { exportGeoJson } from '../../../algorithms/shoreR/geoJsonExport';
import { useRegisterGeoJsonExport, useRegisterPdfExport, useRegisterSessionExport, useThemeMode } from '../../contexts/index.jsx';
import {
  buildMapStyles,
  createShorelineTheme,
  OVERLAY_COLUMN_MAX_HEIGHT,
  OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
  OVERLAY_SIDE_GUTTER,
  OVERLAY_SCROLLBAR_GUTTER,
  OVERLAY_COLUMN_PADDING_TOP,
  OVERLAY_MAIN_COLUMN_WIDTH,
  OVERLAY_SIDE_COLUMN_WIDTH,
  OVERLAY_PANEL_WIDTH,

  OVERLAY_COLUMN_LAYOUT,
} from '../Shoreline/theme.js';
import { buildShoreRReport, REPORT_MODULE } from '../../../algorithms/shoreR/pdfReport';
import { captureImageForReport, renderReport, reportFileName } from '../../../common/pdfReport';
import { APP_ROOT_SELECTOR, settleMapForCapture } from '../../../common/captureElement';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * ShoreR processing page.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:ShoreR
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  mapContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexFlow: 'column',
    ...OVERLAY_COLUMN_LAYOUT,
  },
  panelWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flexShrink: 0,
    overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  mapOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '100%',
    zIndex: 10,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
  },
}));

/**
 * @function ProcessingShoreRPage
 * @description ShoreR processing page. Shows the map with the ROI, image chooser, and shape
 * legend (including projection layers), and hosts the ShoreRAnalysisSteps workflow.
 * @see {@link GoogleMap} - Google Map component
 * @see {@link ShoreRAnalysisSteps} - ShoreR analysis workflow component
 * **Called by:
 * @see {MainPage} - via the `/processing_shore_r` Route.
 * @memberof module:ShoreR
 * @returns {JSX.Element} Complete processing page JSX element
 */
const ProcessingShoreRPage = () => {
  const dispatch = useDispatch();
  const coordinates = useSelector((state) => state.acquisition.coordinates);
  /**
   * The name of the KML the area came from, when it came from one. The export is named after it, so
   * a folder of saved analyses says which stretch of coast each one is.
   */
  const kmlFileName = useSelector((state) => state.acquisition.kmlFileName);
  const satelliteIndex = useSelector((state) => state.acquisition.satelliteIndex);
  const shapesGroup = useSelector((state) => state.map.shapes);
  const shoreRData = useSelector((state) => state.results.shoreRData);
  const chosenImages = useSelector((state) => state.results.chosenImages);
  const baselineData = useSelector((state) => state.results.baselineData);
  const classes = useStyles();
  const [t] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const { mapRef, roiRef, onMapLoad, fitRoi } = useRoiOverlay(coordinates, "#00A391");
  const boundsRef = useRef();
  const drawingManager = useRef();
  const [baseLine, setBaseLine] = useState(null);
  const [baselineVisible, setBaselineVisible] = useState(true);
  const [projectionLegend, setProjectionLegend] = useState([]);
  const [layersPanelOpen, setLayersPanelOpen] = useLocalStorage('shoreRLayersPanelOpen', true);
  const [mapType, setMapType] = useLocalStorage('shoreRMapType', 'roadmap');
  const [labelsVisible, setLabelsVisible] = useLocalStorage('shoreRMapLabels', true);
  const { shorelineThemeMode } = useThemeMode();
  const outerTheme = useTheme();
  const shoreRTheme = useMemo(
    () => createShorelineTheme(shorelineThemeMode, outerTheme),
    [outerTheme, shorelineThemeMode],
  );

  const handleMapTypeChange = (type) => {
    setMapType(type);
    mapRef.current?.setMapTypeId(type);
  };

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({ styles: buildMapStyles({ themeMode: shorelineThemeMode, mapType, labelsVisible }) });
  }, [shorelineThemeMode, mapType, labelsVisible, mapRef]);

  const shoreDates = useMemo(() => {
    const feats = shoreRData?.shorelinesGeoJSON?.features ?? [];
    const ds = feats
      .map((f) => f.properties?.date)
      .filter(Boolean)
      .sort();
    if (!ds.length) return null;
    const fmt = (d) => {
      const dt = new Date(d);
      return `${String(dt.getUTCDate()).padStart(2, '0')}/${String(dt.getUTCMonth() + 1).padStart(2, '0')}/${String(dt.getUTCFullYear()).slice(-2)}`;
    };
    return { start: fmt(ds[0]), end: fmt(ds[ds.length - 1]) };
  }, [shoreRData]);

  /**
   * @function handleBaselineVisibility
   * @description Toggles the drawn baseline's visibility on the map by updating its stroke
   * opacity, and keeps the local baselineVisible state in sync.
   * @param {boolean} visible - Whether the baseline should be shown.
   * **Called by:
   * @see {ShapeList} - via the handleBaselineVisibility prop.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleBaselineVisibility = (visible) => {
    setBaselineVisible(visible);
    baseLine?.overlay?.setOptions({ strokeOpacity: visible ? 1 : 0 });
  };

  /**
   * @function handleMapLoad
   * @description Wraps `useRoiOverlay`'s own `onMapLoad` (draws the ROI) with the session-import
   * replay: a baseline/shoreline/transect data left waiting by `importSessionState` can only be
   * turned into map overlays once a real map exists, which is exactly when this fires.
   * @param {google.maps.Map} map - The now-ready map instance.
   * @see {@link restorePendingBaseline}
   * @see {@link renderPendingImportedShapes}
   * **Called by:
   * @see {GoogleMap} - via its `onLoad` prop.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleMapLoad = (map) => {
    onMapLoad(map);
    map.setMapTypeId(mapType);
    const restoredBaseline = restorePendingBaseline(map);
    if (restoredBaseline) setBaseLine(restoredBaseline);
    renderPendingImportedShapes(dispatch);
  };

  /**
   * @function exportSessionState
   * @description Downloads a JSON snapshot of the current AOI/satellite/selected-images and the
   * already-evaluated shoreline/transect result, for ShoreR's "import session" icon to replay
   * later — a developer/testing shortcut so QA doesn't have to redo the whole analysis every time
   * they need a completed result to test against.
   * @see {@link buildSessionExport}
   * **Called by:
   * @see {NavigationBar} - via the standardized "export session for testing" icon's onClick.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const exportSessionState = () => {
    exportSession(
      buildSessionExport({
        satelliteIndex,
        coordinates,
        chosenImages,
        baselineCoords: baselineData?.coords || null,
        shoreRData,
      }),
      kmlFileName,
    );
  };
  useRegisterSessionExport('shore_r', shoreRData ? exportSessionState : null);

  /**
   * @function exportGeoJsonLayers
   * @description Writes the analysis's geometry - area, baseline, transects with their rates, coastlines - to a
   * GeoJSON file for a GIS.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const exportGeoJsonLayers = () => {
    if (!exportGeoJson({ coordinates, baselineCoords: baselineData?.coords || null, shoreRData })) notifyError(t('forms.acquisition.geoJsonExport.empty'));
  };
  useRegisterGeoJsonExport('shore_r', shoreRData ? exportGeoJsonLayers : null);

  /**
   * @function reportText
   * @description Every piece of wording the report needs, translated here and handed to the
   * builder, which stays a pure function of the analysis - see {@link buildShoreRReport}.
   * @memberof module:Core
   * @returns {Object}
   */
  const reportText = () => {
    const key = (name) => `forms.core.pdfReport.${name}`;
    const own = (name) => `forms.core.pdfReport.shore_r.${name}`;
    const group = (name) => t(own(name), { returnObjects: true, defaultValue: t(key(name), { returnObjects: true }) });
    return {
      ...t(key('common'), { returnObjects: true }),
      ...t(own('text'), { returnObjects: true }),
      subtitle: t(key('subtitle'), { date: new Date().toLocaleString() }),
      missingImage: (caption) => t(key('missingImage'), { caption }),
      screenCaption: t(key('screenCaption')),
      rates: group('rates'),
      classes: group('classes'),
      transects: group('transects'),
      transectNote: {
        complete: (values) => t(key('transectComplete'), values),
        truncated: (values) => t(key('transectTruncated'), values),
      },
      transectComplete: (values) => t(key('transectComplete'), values),
      transectTruncated: (values) => t(key('transectTruncated'), values),
      forecastYears: (values) => t(key('forecastYears'), values),
      missingTideNote: (values) => t(own('missingTideNote'), values),
      tide: {
        range: (values) => t(own('tideRangeValue'), values),
        span: (values) => t(own('tideSpanValue'), values),
      },
      errors: group('errors'),
      byDate: group('byDate'),
      byTransect: group('byTransect'),
      bays: group('bays'),
      controlPoints: group('controlPoints'),
      waterlines: group('waterlines'),
      cover: group('cover'),
    };
  };

  /**
   * @function exportPdfReport
   * @description Writes the whole analysis to one PDF.
   * The map is captured from the screen rather than fetched, because what this module draws is a
   * Google Maps overlay and not an image with a URL - see {@link captureImageForReport}.
   * @memberof module:Core
   * @returns {Promise<void>}
   */
  const exportPdfReport = async () => {
    try {
      const text = reportText();
      /**
       * The map is captured from the screen, so it is framed on the analysis first - otherwise the
       * report shows wherever the user had last panned to. See {@link settleMapForCapture}, which
       * also waits for the tiles of the new view to arrive.
       */
      await settleMapForCapture(mapRef.current, fitRoi);
      const screen = await captureImageForReport(APP_ROOT_SELECTOR, text.screenCaption);
      const report = buildShoreRReport({
        coordinates,
        baselineCoords: baselineData?.coords || null,
        shoreRData,
        chosenImages,
        kmlFileName,
        images: [screen],
        text,
        className: (value) => t(`forms.core.pdfReport.esteves.${value}`, { defaultValue: value }),
      });
      renderReport(report).save(reportFileName(REPORT_MODULE));
    } catch (error) {
      console.error('exportPdfReport error:', error);
      notifyError(t('forms.core.pdfReport.failed'));
    }
  };
  useRegisterPdfExport(REPORT_MODULE, shoreRData ? exportPdfReport : null);


  return (
    <ThemeProvider theme={shoreRTheme}>
    <Box className={classes.wrapper}>
      <Grid container justifyContent="space-between" alignItems="flex-start" spacing={0} className={classes.mapOverlay}>
        <Grid
          item
          xs={9}
          className={classes.mapContainer}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: 8,
            flexBasis: 'auto',
            flexGrow: 0,
            width: OVERLAY_MAIN_COLUMN_WIDTH,
            maxWidth: OVERLAY_MAIN_COLUMN_WIDTH,
            paddingTop: OVERLAY_COLUMN_PADDING_TOP,
            paddingLeft: OVERLAY_SIDE_GUTTER,
            paddingRight: OVERLAY_SCROLLBAR_GUTTER,
            height: '100%',
            overflowY: 'auto',
            maxHeight: OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
          }}>
          <OverlayPanelsToggle side="left" />
          <Paper className={classes.panelWrapper} elevation={1}>
            <Box className={classes.panelHeader}>
              <Box display="flex" alignItems="center" gridGap={6}>
                <LayersOutlined fontSize="small" color="primary" />
                <Typography variant="h6">{t('forms.shorelineAnalysis.layersPanelTitle')}</Typography>
              </Box>
              <IconButton size="small" onClick={() => setLayersPanelOpen((prev) => !prev)}>
                {layersPanelOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Box>
            <Collapse in={layersPanelOpen}>
              <ShapeList
                embedded
                shapesGroup={shapesGroup}
                baseLine={baseLine}
                roiCoords={roiRef}
                drawingManager={drawingManager}
                baselineVisible={baselineVisible}
                handleBaselineVisibility={handleBaselineVisibility}
                colorbarData={shoreDates}
                customLayers={projectionLegend}
                onCustomLayerToggle={(layerId, visible) => {
                  setProjectionLegend((prev) =>
                    prev.map((group) => ({
                      ...group,
                      children: group.children?.map((child) => {
                        if (child.id === layerId) {
                          child.polyline?.setVisible(visible);
                          child.polylines?.forEach((p) => p.setVisible(visible));
                          return { ...child, visible };
                        }
                        return child;
                      }),
                    })),
                  );
                }}
              />
            </Collapse>
          </Paper>

          <ShoreRAnalysisSteps
            t={t}
            mapRef={mapRef}
            roiCoords={roiRef}
            bounds={boundsRef}
            baseLine={baseLine}
            setBaseLine={setBaseLine}
            drawingManager={drawingManager}
            baselineVisible={baselineVisible}
            onProjectionLegendChange={setProjectionLegend}
          />

        </Grid>
        <Grid
          item
          xs={3}
          style={{
            display: 'flex',
            flexDirection: 'column',
            ...OVERLAY_COLUMN_LAYOUT,
            gap: 8,
            flexBasis: 'auto',
            flexGrow: 0,
            width: OVERLAY_SIDE_COLUMN_WIDTH,
            maxWidth: OVERLAY_SIDE_COLUMN_WIDTH,
            marginLeft: 'auto',
            paddingTop: OVERLAY_COLUMN_PADDING_TOP,
            paddingLeft: OVERLAY_SCROLLBAR_GUTTER,
            paddingRight: OVERLAY_SIDE_GUTTER,
            overflowY: 'auto',
            maxHeight: OVERLAY_COLUMN_MAX_HEIGHT,
          }}>
          <OverlayPanelsToggle side="right" />
          <BackgroundMapPanel
            mapType={mapType}
            onMapTypeChange={handleMapTypeChange}
            labelsVisible={labelsVisible}
            onLabelsVisibleChange={setLabelsVisible}
            t={t}
          />
          <LoadedImagesAccordion />
          <ImageChooserCard vertical="shore-r" />
        </Grid>
      </Grid>
      <ReturnButtons lastStepPath="/main/shore_r/3" />
      <GoogleMap
        onLoad={handleMapLoad}
        onBoundsChanged={(bounds) => {
          boundsRef.current = bounds;
        }}
        style={{ position: 'absolute' }}
        mapOptions={{ mapTypeControl: false }}
      />
    </Box>
    </ThemeProvider>
  );
};

export default ProcessingShoreRPage;
